import { Fragment, useCallback } from 'react'
import { v4 as uuid } from 'uuid'
import { CheckboxListCheckbox } from '~/components/v2/CheckboxList/CheckboxListCheckbox'
import { CheckboxSubsection } from '~/components/v2/CheckboxSubsection'
import { Flex, ScrollShadowBox, Spacer, Text, Tooltip } from '~/design_system'
import { Question } from '~/icons'
import { useLatestCallbackRef } from '~/hooks/useLatestRef'
import { $thm } from '~/common/themeSx'

export function List({ options, checkedItems, onChange, isDisabled, sx, onItemsRendered, ref, ...props }) {
  const last = (options?.length ?? 0) - 1
  const notifyVisibleRange = () => {
    if (!onItemsRendered || last < 0) return
    onItemsRendered({
      visibleStartIndex: 0,
      visibleStopIndex: last,
      overscanStartIndex: 0,
      overscanStopIndex: last
    })
  }

  return (
    <ScrollShadowBox
      ref={ref}
      onScroll={event => {
        const el = event.currentTarget
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) notifyVisibleRange()
      }}
      sx={{
        ...$thm({
          bg: { $base: 'gray.50', $canvas: 'transparent' },
          pl: { $base: 1, $canvas: 0 },
          py: { $base: 2, $canvas: 0 },
          ml: { $base: -1, $canvas: 0 },
          my: { $base: -2, $canvas: 0 },
          // Horizontal only: negative vertical margin on the scrollport paints rows past the edge shadows.
          '& > [data-scroll-shadow-box]': {
            w: { $canvas: 'calc(100% + var(--chakra-space-3))' },
            ml: { $canvas: -3 },
            pl: { $canvas: 3 }
          }
        }),
        ...sx
      }}
      {...props}>
      {options?.map(({ index, style }) => (
        <ListCheckbox
          key={options[index].value}
          config={options[index]}
          checkedItems={checkedItems}
          isDisabled={isDisabled}
          onChange={onChange}
          style={style}
        />
      ))}
    </ScrollShadowBox>
  )
}

List.displayName = 'CheckBoxList.List'

function ListCheckbox({ config, checkedItems, isDisabled, onChange, style }) {
  const { text, subtext, value, helperIndicator, index: originalIndex, toolTip } = config
  const subtextId = `subtext-${uuid()}`
  const ariaProps = {}
  if (subtext) ariaProps['aria-describedby'] = subtextId
  const onChangeProp = useLatestCallbackRef(onChange)
  /*
      eslint-disable-next-line react-hooks/preserve-manual-memoization --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: `onChangeProp` is used inside the callback but not in deps.
      Possible fix approach:
        // Option 1: Add onChangeProp to deps (it's stable so this is safe):
        const onChangeFn = useCallback(e => onChangeProp(e, originalIndex), [originalIndex, onChangeProp])

        // Option 2: Remove useCallback entirely - the memoization is not needed (verify performance is acceptable):
        const onChangeFn = e => onChangeProp(e, originalIndex)
  */
  const onChangeFn = useCallback(e => onChangeProp(e, originalIndex), [originalIndex])

  return (
    <Fragment key={value}>
      <Flex>
        <CheckboxListCheckbox
          {...ariaProps}
          data-testid={`status-checkbox-filter-${value}`}
          w={toolTip ? '86%' : '100%'}
          mb={subtext ? '0' : '3'}
          style={style}
          value={value}
          isChecked={checkedItems[originalIndex]}
          isDisabled={isDisabled}
          onChange={onChangeFn}>
          {text}
        </CheckboxListCheckbox>
        {helperIndicator && (
          <>
            <Spacer />
            <Text display="inline" color="gray.500" mr={2} fontSize="md" data-testid={`helper-indicator-${value}`}>
              {helperIndicator}
            </Text>
          </>
        )}
        {toolTip && (
          <Flex>
            <Tooltip label={toolTip} placement="top-start">
              <Question w={6} color="blue.500" cursor="help" />
            </Tooltip>
          </Flex>
        )}
      </Flex>
      {subtext && (
        <CheckboxSubsection id={subtextId} size="lg" color="gray.500" mb="2">
          <Text>{subtext}</Text>
        </CheckboxSubsection>
      )}
    </Fragment>
  )
}
